// useReplay — the playback engine. Schedules a BattleResult's events
// over per-turn wall-clock budgets per spec §12.5. Supports speed
// multipliers, pause/resume, skip-to-end, and restart. All controls
// are synchronous and local; no server round-trips during replay.

import { useEffect, useMemo, useReducer, useRef, useState } from 'react'
import type { BattleEvent, BattleResult, UnitRef } from '@origami/shared'
import type {
  BattleDisplayState,
  ReplayControls,
  ReplayProgress,
  ReplaySpeed,
  UnitVisualState,
} from './types'
import { applyEvent, applyEvents, initialState } from './handlers'

interface UseReplayOptions {
  /** Wall-clock budget per turn at 1× speed. Default 2800ms — gives
   *  each event (windup, strike, damage, hit, post-strike) enough
   *  breathing room to read without overlapping. 2×/4× divide this. */
  turnBudgetMs?: number
  /** Initial speed multiplier. Default 1×. */
  initialSpeed?: ReplaySpeed
  /** Whether to start playing immediately on mount. Default true. */
  autoplay?: boolean
}

/** Group the event log by turn — preserving relative order within a turn. */
function groupByTurn(events: BattleEvent[]): BattleEvent[][] {
  if (events.length === 0) return []
  const groups: BattleEvent[][] = []
  let current: BattleEvent[] = []
  let currentTurn = events[0]?.turn ?? 0
  for (const e of events) {
    if (e.turn !== currentTurn) {
      if (current.length) groups.push(current)
      current = []
      currentTurn = e.turn
    }
    current.push(e)
  }
  if (current.length) groups.push(current)
  return groups
}

/** A single "intro" step the player can visually digest before turn 1 —
 *  one synergy applying, or one unit's on_unfold firing. The anchor is
 *  the headline event (synergy_applied / trigger_fired); the
 *  consequents are the stat_changed / heal / damage events the sim
 *  emits in tow that should fire at the same wall-clock moment so the
 *  sidebar chip flash and the per-unit floats land together. */
interface IntroStep {
  anchor: BattleEvent
  consequents: BattleEvent[]
}

/** True for the events that headline a discrete intro step — the
 *  ones that get their own 400ms slot. Everything between two anchors
 *  is bundled into the preceding anchor's consequents. */
function isIntroAnchor(e: BattleEvent): boolean {
  if (e.type === 'synergy_applied') return true
  if (e.type === 'trigger_fired' && e.trigger === 'on_unfold') return true
  return false
}

/** Pull the team off an anchor. synergy_applied carries team directly;
 *  trigger_fired carries a `source` UnitRef shaped `<team>:<position>`. */
function getAnchorTeam(e: BattleEvent): 'A' | 'B' | null {
  if (e.type === 'synergy_applied') return e.team
  if (e.type === 'trigger_fired') {
    const src = e.source
    return src.startsWith('A:') ? 'A' : src.startsWith('B:') ? 'B' : null
  }
  return null
}

/** Build the visually-paced intro sequence from turn 0's events. The
 *  sim emits at turn 0 in "all-team-A synergies → all-team-B
 *  synergies → all-team-A on_unfolds → all-team-B on_unfolds" order
 *  (with priority-team first); we re-interleave so synergies and
 *  on_unfolds each play in strict A/B alternation, priority team
 *  first. Each anchor sweeps its consequent stat_changed / heal /
 *  damage events along with it so the chip flash + per-unit floats
 *  appear at the same step. */
function buildIntroSteps(turn0Events: BattleEvent[]): IntroStep[] {
  // Drop battle_start — it's applied synchronously in the reducer
  // init so the units render on the stage immediately.
  const deferred = turn0Events.filter((e) => e.type !== 'battle_start')

  // Walk linearly, packing each anchor with the run of non-anchor
  // events that follow it (until the next anchor or end of turn 0).
  const rawSteps: IntroStep[] = []
  let i = 0
  while (i < deferred.length) {
    const e = deferred[i]!
    if (isIntroAnchor(e)) {
      const consequents: BattleEvent[] = []
      let j = i + 1
      while (j < deferred.length && !isIntroAnchor(deferred[j]!)) {
        consequents.push(deferred[j]!)
        j++
      }
      rawSteps.push({ anchor: e, consequents })
      i = j
    } else {
      i++ // drop orphan non-anchors (shouldn't happen with sim today)
    }
  }

  if (rawSteps.length === 0) return []

  // Detect priority from the first anchor — the sim already emits
  // the priority team's events first per ctx.priorityA, so the first
  // anchor's team IS the priority team.
  const priorityTeam = getAnchorTeam(rawSteps[0]!.anchor) ?? 'A'

  const synergies = rawSteps.filter((s) => s.anchor.type === 'synergy_applied')
  const unfolds = rawSteps.filter(
    (s) => s.anchor.type === 'trigger_fired',
  )

  // Strict alternation: priority team's first item, other team's
  // first item, priority team's second, etc. When teams have
  // uneven counts the longer team's tail plays solo at the end.
  function interleave(steps: IntroStep[]): IntroStep[] {
    const prio = steps.filter((s) => getAnchorTeam(s.anchor) === priorityTeam)
    const other = steps.filter((s) => getAnchorTeam(s.anchor) !== priorityTeam)
    const out: IntroStep[] = []
    const max = Math.max(prio.length, other.length)
    for (let k = 0; k < max; k++) {
      if (prio[k]) out.push(prio[k]!)
      if (other[k]) out.push(other[k]!)
    }
    return out
  }

  // Synergies first (full sequence), then on_unfolds — matches the
  // game's mental model where synergies "lock in" the team's starting
  // bonuses before any units act.
  return [...interleave(synergies), ...interleave(unfolds)]
}

/** Within a turn, decide what fraction of the turn-budget each event
 *  should run at. Per §12.5 the turn breathes through phases:
 *  pre-strike triggers → strike pair → damage → post-strike → crumple
 *  → slide_forward / summon. Returns offsets in [0..1] of the budget. */
function scheduleTurnOffsets(events: BattleEvent[]): number[] {
  // Identify the "strike phase": the first index of a strike event.
  const strikeIdx = events.findIndex((e) => e.type === 'strike')
  const slideIdx = events.findIndex((e) => e.type === 'slide_forward' || e.type === 'summon')

  return events.map((e, i) => {
    // turn_start: instant (start of budget)
    if (e.type === 'turn_start') return 0
    // synergy_applied / battle_start happen at turn 0 only — instant
    if (e.type === 'battle_start' || e.type === 'synergy_applied') return 0
    // turn_end / battle_end: end of budget so the visuals settle first
    if (e.type === 'turn_end' || e.type === 'battle_end') return 0.95
    // crumple: late in the turn (post-damage)
    if (e.type === 'crumple') return 0.7
    // gold_earned: fires as a consequence of on_crumple (gold_fish).
    // Slot just after crumple so the chronicle reads "fish crumples →
    // +N gold next round" in the right order.
    if (e.type === 'gold_earned') return 0.72
    // slide_forward: very end of turn so deaths animate first
    if (e.type === 'slide_forward') return 0.88
    // summon: AFTER slide_forward so the lineup has re-flowed before
    // the new unit's unfold-in plays. Otherwise the token spawns at
    // its `event.position` displayPosition while the survivors are
    // still occupying their old slots — they visually overlap and
    // the token reads as "respawning in place of the dead parent"
    // instead of "appearing at the back of the line." 0.92 puts the
    // summon just after slide_forward (0.88) but before turn_end
    // (0.95). The unfold-in CSS animation runs 1.1s past the turn
    // boundary; turn_end below preserves the 'summoning' visualState
    // so the animation completes uninterrupted.
    if (e.type === 'summon') return 0.92
    // pre-strike triggers: first 20%
    if (strikeIdx >= 0 && i < strikeIdx) {
      return 0.05 + (i / Math.max(1, strikeIdx)) * 0.15
    }
    // strike + damage cluster: 25–55% of budget
    if (e.type === 'strike') return 0.28
    if (e.type === 'damage') return 0.42
    // post-strike triggers, heal, stat_changed, status_*: 55–70%
    const postStart = strikeIdx >= 0 ? strikeIdx + 1 : 0
    const postEnd = slideIdx >= 0 ? slideIdx : events.length
    const postCount = Math.max(1, postEnd - postStart)
    if (i >= postStart && i < postEnd) {
      const local = i - postStart
      return 0.55 + (local / postCount) * 0.13
    }
    return 0.5
  })
}

type ReducerAction =
  | { type: 'apply'; event: BattleEvent }
  | { type: 'apply_many'; events: BattleEvent[] }
  | { type: 'reset'; state: BattleDisplayState }
  | { type: 'prune_floats'; before: number }
  | {
      type: 'set_visual_state'
      target: UnitRef
      state: UnitVisualState
      /** If provided, only apply the change when the unit's current
       *  visualState matches this value. Used by deferred timers
       *  (e.g. summon → idle 1.1s later) so they don't overwrite
       *  another deliberate state change (e.g. battle_end → victory)
       *  that happened in the meantime. */
      from?: UnitVisualState
    }
  | { type: 'clear_pulse'; target: UnitRef }

function reducer(state: BattleDisplayState, action: ReducerAction): BattleDisplayState {
  switch (action.type) {
    case 'apply':
      return applyEvent(state, action.event)
    case 'apply_many':
      return applyEvents(state, action.events)
    case 'reset':
      return action.state
    case 'prune_floats':
      return {
        ...state,
        floatingNumbers: state.floatingNumbers.filter((f) => f.spawnedAt >= action.before),
        impactBursts: state.impactBursts.filter((b) => b.spawnedAt >= action.before),
      }
    case 'set_visual_state': {
      const u = state.units[action.target]
      // Don't override death/gone — those visualStates are terminal
      // for a unit and the windup pre-schedule could still fire after
      // a unit has died if pacing is unusual.
      if (!u || u.visualState === 'death' || u.visualState === 'gone') return state
      // Guard: deferred-timer dispatches pass `from` so they only
      // apply when the unit is still in the expected state. Prevents
      // a late summon → idle settle from overwriting a battle_end →
      // victory that already fired in between.
      if (action.from !== undefined && u.visualState !== action.from) return state
      return {
        ...state,
        units: {
          ...state.units,
          [action.target]: { ...u, visualState: action.state },
        },
      }
    }
    case 'clear_pulse': {
      const u = state.units[action.target]
      if (!u) return state
      return {
        ...state,
        units: {
          ...state.units,
          [action.target]: { ...u, pulseEffect: null },
        },
      }
    }
  }
}

interface UseReplayReturn {
  state: BattleDisplayState
  progress: ReplayProgress
  controls: ReplayControls
}

const FLOAT_TTL_MS = 1100
/** Wall-clock delay between intro steps (synergies + on_unfolds) at
 *  1× speed. Scales inversely with replay speed so 2× → 200ms,
 *  4× → 100ms. Lets the player register the sidebar chip flash + the
 *  per-unit floats before the next step lands. */
const INTRO_STEP_MS = 400

export function useReplay(
  battle: BattleResult,
  options: UseReplayOptions = {}
): UseReplayReturn {
  const turnBudget = options.turnBudgetMs ?? 2800
  const initialSpeed = options.initialSpeed ?? 1
  const autoplay = options.autoplay ?? true

  const turns = useMemo(() => groupByTurn(battle.event_log), [battle])
  const introSteps = useMemo(() => buildIntroSteps(turns[0] ?? []), [turns])

  // Display state — driven exclusively by the event reducer.
  const [state, dispatch] = useReducer(reducer, undefined as unknown as BattleDisplayState, () => {
    // Apply ONLY battle_start at init so the units render on the
    // stage. Synergies + on_unfold triggers (and their stat_changed
    // consequents) are scheduled via the intro phase below so the
    // player can visually digest each one. If there are no intro
    // steps (which shouldn't really happen — every battle has at
    // least one synergy or on_unfold — but keep a fallback), apply
    // the whole turn 0 so we don't strand events.
    const turn0 = turns[0] ?? []
    const battleStart = turn0.find((e) => e.type === 'battle_start')
    const intro = buildIntroSteps(turn0)
    if (intro.length === 0) return applyEvents(initialState(), turn0)
    return battleStart
      ? applyEvent(initialState(), battleStart)
      : initialState()
  })

  // Timeline cursor — turnIdx 0 = the intro phase, 1+ = regular
  // turns. When there are no intro steps we skip straight to turn 1.
  const [turnIdx, setTurnIdx] = useState(
    introSteps.length > 0 ? 0 : turns.length > 0 ? 1 : 0,
  )
  const [speed, setSpeedState] = useState<ReplaySpeed>(initialSpeed)
  const [isPlaying, setIsPlaying] = useState(autoplay && turns.length > 1)
  // How many intro steps have applied — drives the timeline fill
  // through the otherwise turn-less synergy phase.
  const [introStep, setIntroStep] = useState(0)
  // Bumped by seekToTurn so the play loop reschedules even when the
  // seek lands on the turn already in progress (timers were cleared).
  const [seekNonce, setSeekNonce] = useState(0)

  // Timer handles — cleared on pause/skip/unmount.
  const timersRef = useRef<number[]>([])
  const turnAdvanceRef = useRef<number | null>(null)

  function clearTimers() {
    for (const id of timersRef.current) window.clearTimeout(id)
    timersRef.current = []
    if (turnAdvanceRef.current !== null) {
      window.clearTimeout(turnAdvanceRef.current)
      turnAdvanceRef.current = null
    }
  }

  // Play loop: when isPlaying flips on, schedule the current turn's events.
  useEffect(() => {
    if (!isPlaying) return

    // Intro phase — synergies + on_unfolds, one anchor per ~400ms
    // (scaled by speed). After the last step lands we advance to
    // turn 1 and the regular per-turn schedule takes over. Each
    // anchor + its consequents apply together so the chip flash and
    // unit floats land at the same moment.
    if (turnIdx === 0) {
      if (introSteps.length === 0) {
        setTurnIdx(1)
        return
      }
      const stepDelayMs = INTRO_STEP_MS / speed
      const pulseDurationMs = 1000 / speed
      introSteps.forEach((step, i) => {
        const delay = i * stepDelayMs
        const applyHandle = window.setTimeout(() => {
          dispatch({ type: 'apply', event: step.anchor })
          for (const c of step.consequents) {
            dispatch({ type: 'apply', event: c })
          }
          setIntroStep(i + 1)
        }, delay)
        timersRef.current.push(applyHandle)
        // Clear transient pulse highlights so the next step's
        // buffed_up / healed glow doesn't carry forward.
        for (const e of [step.anchor, ...step.consequents]) {
          const isPulsedDamage =
            e.type === 'damage' && (e.mitigated ?? 0) > 0
          if (e.type === 'heal' || e.type === 'stat_changed' || isPulsedDamage) {
            const target = e.target
            const clearHandle = window.setTimeout(() => {
              dispatch({ type: 'clear_pulse', target })
            }, delay + pulseDurationMs)
            timersRef.current.push(clearHandle)
          }
        }
      })
      // Transition to turn 1 after the last step gets a brief
      // settle (200ms / speed) so the final flash isn't immediately
      // clobbered by turn_start resetting visualStates.
      const introTotalMs =
        introSteps.length * stepDelayMs + 200 / speed
      turnAdvanceRef.current = window.setTimeout(() => {
        setTurnIdx(1)
      }, introTotalMs)
      return clearTimers
    }

    if (turnIdx >= turns.length) {
      setIsPlaying(false)
      return
    }
    const events = turns[turnIdx]
    if (!events) return
    const offsets = scheduleTurnOffsets(events)
    const effectiveBudget = turnBudget / speed
    // Per-effect lead/trail durations scale with replay speed so
    // transient visual beats (windup anticipation, unfold-in settle,
    // pulse flares) stay visible at 2× / 4× rather than vanishing.
    const windupLeadMs = 150 / speed
    const unfoldDurationMs = 1150 / speed
    const pulseDurationMs = 1000 / speed
    events.forEach((e, i) => {
      const offset = offsets[i] ?? 0
      const delay = Math.max(0, offset * effectiveBudget)
      // Pre-windup: for strike events, set the source to attack_windup
      // ~150ms before the strike's damage applies, so the unit has a
      // visible anticipation beat before lunging.
      if (e.type === 'strike') {
        const windupDelay = Math.max(0, delay - windupLeadMs)
        const source = e.source
        const windupHandle = window.setTimeout(() => {
          dispatch({ type: 'set_visual_state', target: source, state: 'attack_windup' })
        }, windupDelay)
        timersRef.current.push(windupHandle)
      }
      const handle = window.setTimeout(() => {
        dispatch({ type: 'apply', event: e })
      }, delay)
      timersRef.current.push(handle)
      // Post-summon: revert to 'idle' once the .unfold-in animation
      // has finished so the unit picks up the regular bob loop. The
      // `from: 'summoning'` guard makes this a no-op if something
      // else (turn_end, battle_end) already updated the unit's
      // visualState in the meantime.
      if (e.type === 'summon') {
        // Use the event's allocated ref (invariant: never reconstruct
        // from team+position — collision summons carry a "#N" suffix,
        // e.g. a phoenix re-summoning into its own old slot).
        const ref = e.ref as UnitRef
        const settleHandle = window.setTimeout(() => {
          dispatch({
            type: 'set_visual_state',
            target: ref,
            state: 'idle',
            from: 'summoning',
          })
        }, delay + unfoldDurationMs)
        timersRef.current.push(settleHandle)
      }
      // Post-pulse: clear the transient pulseEffect (healed,
      // buffed_up, buffed_down, mitigated) after the animation runs
      // so the glow doesn't linger and so the next event re-triggers
      // it. `damage` only carries a pulse when mitigation actually
      // applied (stone_tortoise plate).
      const isPulsedDamage = e.type === 'damage' && (e.mitigated ?? 0) > 0
      if (e.type === 'heal' || e.type === 'stat_changed' || isPulsedDamage) {
        const target = e.target
        const clearHandle = window.setTimeout(() => {
          dispatch({ type: 'clear_pulse', target })
        }, delay + pulseDurationMs)
        timersRef.current.push(clearHandle)
      }
    })
    // Schedule the turn advance itself.
    turnAdvanceRef.current = window.setTimeout(() => {
      setTurnIdx((idx) => idx + 1)
    }, effectiveBudget)
    return clearTimers
    // We deliberately re-run when turnIdx, speed, or isPlaying
    // change. introSteps is referenced by the intro branch above.
    // seekNonce forces a reschedule when a seek lands on the SAME
    // turnIdx (its timers were cleared but no dep changed).
  }, [isPlaying, turnIdx, speed, turnBudget, turns, introSteps, seekNonce])

  // Auto-stop when we run past the end.
  useEffect(() => {
    if (turnIdx >= turns.length) setIsPlaying(false)
  }, [turnIdx, turns.length])

  // Periodic pruning of expired floating numbers so the array doesn't grow.
  useEffect(() => {
    const interval = window.setInterval(() => {
      dispatch({ type: 'prune_floats', before: performance.now() - FLOAT_TTL_MS })
    }, 500)
    return () => window.clearInterval(interval)
  }, [])

  /** Deterministic state for "paused at the start of turn group
   *  `targetIdx`": every event before it applied instantly, then the
   *  transient presentation residue stripped (floats, bursts, pulses,
   *  mid-unfold states) so renderers don't replay a backlog. */
  function buildSeekState(targetIdx: number): BattleDisplayState {
    const base =
      targetIdx === 0
        ? (() => {
            // Mirror restart: battle_start only; the intro replays live.
            const battleStart = (turns[0] ?? []).find((e) => e.type === 'battle_start')
            return battleStart ? applyEvent(initialState(), battleStart) : initialState()
          })()
        : applyEvents(initialState(), turns.slice(0, targetIdx).flat())
    const units: BattleDisplayState['units'] = {}
    for (const [ref, u] of Object.entries(base.units)) {
      units[ref as UnitRef] = {
        ...u,
        pulseEffect: null,
        visualState: u.visualState === 'summoning' ? 'idle' : u.visualState,
      }
    }
    return {
      ...base,
      units,
      floatingNumbers: [],
      impactBursts: [],
      // Group k holds turn k's events — display the turn about to play.
      currentTurn: Math.min(targetIdx, Math.max(0, turns.length - 1)),
    }
  }

  const controls: ReplayControls = {
    play: () => {
      if (turnIdx >= turns.length) return
      setIsPlaying(true)
    },
    pause: () => {
      clearTimers()
      setIsPlaying(false)
    },
    toggle: () => {
      if (isPlaying) {
        clearTimers()
        setIsPlaying(false)
      } else if (turnIdx < turns.length) {
        setIsPlaying(true)
      }
    },
    setSpeed: (s) => {
      // Cancel current schedule; effect will re-run with the new speed.
      clearTimers()
      setSpeedState(s)
    },
    skipToEnd: () => {
      clearTimers()
      // When mid-intro (turnIdx 0), the only turn-0 events still
      // unapplied are the deferred synergy / on_unfold steps —
      // battle_start was already applied at init, so don't re-apply
      // it (it'd reset HP back to start).
      const remaining =
        turnIdx === 0
          ? [
              ...((turns[0] ?? []).filter((e) => e.type !== 'battle_start')),
              ...turns.slice(1).flat(),
            ]
          : turns.slice(turnIdx).flat()
      if (remaining.length) dispatch({ type: 'apply_many', events: remaining })
      setTurnIdx(turns.length)
      setIsPlaying(false)
    },
    restart: () => {
      clearTimers()
      const turn0 = turns[0] ?? []
      // Mirror the init behavior — if there's an intro to play,
      // reset to JUST battle_start and replay the intro from step
      // 0; otherwise apply all of turn 0 as before.
      const battleStart = turn0.find((e) => e.type === 'battle_start')
      const fresh =
        introSteps.length > 0 && battleStart
          ? applyEvent(initialState(), battleStart)
          : applyEvents(initialState(), turn0)
      dispatch({ type: 'reset', state: fresh })
      setIntroStep(0)
      setTurnIdx(
        introSteps.length > 0 ? 0 : turns.length > 0 ? 1 : 0,
      )
      setIsPlaying(autoplay && turns.length > 1)
    },
    seekToTurn: (turn) => {
      clearTimers()
      const target = Math.max(0, Math.min(turns.length, Math.round(turn)))
      const wasFinished = state.finished || turnIdx >= turns.length
      dispatch({ type: 'reset', state: buildSeekState(target) })
      setIntroStep(target === 0 ? 0 : introSteps.length)
      setTurnIdx(target)
      if (target >= turns.length) {
        setIsPlaying(false)
      } else if (wasFinished) {
        // Scrubbing back from a finished replay resumes playback —
        // otherwise the rebuilt state just sits there looking stalled.
        // A deliberate mid-battle pause is respected.
        setIsPlaying(true)
      }
      // Same-turn seeks change no other dep — force a reschedule.
      setSeekNonce((n) => n + 1)
    },
    stepBack: () => {
      controls.seekToTurn(Math.max(0, turnIdx - 1))
    },
  }

  // Cleanup on unmount.
  useEffect(() => clearTimers, [])

  const progress: ReplayProgress = {
    currentTurn: state.currentTurn,
    totalTurns: turns.length > 0 ? (turns[turns.length - 1]?.[0]?.turn ?? 0) : 0,
    isPlaying,
    isFinished: state.finished || turnIdx >= turns.length,
    speed,
    introFraction:
      introSteps.length === 0 || turnIdx > 0
        ? 1
        : Math.min(1, introStep / introSteps.length),
  }

  return { state, progress, controls }
}
